import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, Wrench } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Btn, Field, Input } from "../components/kit";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [portal, setPortal] = useState(loc.pathname.startsWith("/admin") ? "admin" : "specialist");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password, portal });
      login(data.token, data.user);
      nav("/");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col justify-center px-6">
      <div className="w-full max-w-sm mx-auto fade-in">
        <div className="mb-10">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-neutral-500 mb-3">Mobile mechanics & detailing</div>
          <h1 className="font-head text-5xl font-bold uppercase leading-none" data-testid="login-brand">
            Driven<span className="text-[#FF6B00]">.</span>
            <br />Auto Care
          </h1>
        </div>
        <div className="grid grid-cols-2 gap-1 p-1 mb-6 bg-[#111] rounded-2xl border border-[#262626]" data-testid="login-portal-switch">
          {[["specialist", Wrench, "Specialist"], ["admin", ShieldCheck, "Admin"]].map(([k, I, l]) => (
            <button key={k} type="button" onClick={() => { setPortal(k); setErr(""); nav(k === "admin" ? "/admin/login" : "/login", { replace: true }); }}
              data-testid={`login-portal-${k}`}
              className={`h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${portal === k ? "bg-[#1f1f1f] text-white" : "text-neutral-500"}`}>
              <I className={`w-4 h-4 ${portal === k ? "text-[#FF6B00]" : ""}`} />{l}
            </button>
          ))}
        </div>
        <p className="text-sm text-neutral-400 mb-4" data-testid="login-portal-hint">
          {portal === "admin" ? "Office sign-in: manage jobs, pricing, team and records." : "Mechanics & detailers: see offers and your assigned jobs."}
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <Field label="Email"><Input data-testid="login-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
          <Field label="Password"><Input data-testid="login-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
          {loc.state?.reset && <div className="text-sm text-[#10B981]" data-testid="login-reset-success">Password updated. Sign in with your new password.</div>}
          {err && <div className="text-sm text-red-400" data-testid="login-error">{err}</div>}
          <Btn data-testid="login-submit-btn" size="lg" className="w-full" loading={busy}>{portal === "admin" ? "Sign in as admin" : "Sign in as specialist"}</Btn>
          <Link to={`/forgot-password?portal=${portal}`} className="block text-center text-sm text-neutral-500 hover:text-white" data-testid="forgot-password-link">Forgot password?</Link>
        </form>
        {portal === "specialist" && (
          <div className="mt-8 text-sm text-neutral-500">
            New mobile mechanic or detailer?{" "}
            <Link to="/apply" className="text-[#FF6B00] hover:underline" data-testid="apply-link">Apply to join</Link>
          </div>
        )}
      </div>
    </div>
  );
}
